import { useMemo } from 'react'
import { useBedbankPackageViewsForIndividualRooms } from 'hooks/useBedbankPackageViews'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import type { RoomWithData } from 'hooks/OfferPage/useBedbankBuyCallbacks'

/**
 * Returns `RoomWithData[]` for multi-room individual bedbank bookings
 * by resolving each room's selected package view and rate from per-room
 * package views.
 *
 * This is the single source of truth for per-room rate data. All components
 * that display multi-room pricing (buy section, sidebar summary, header,
 * sticky footer) should derive their prices from this hook so they stay
 * consistent.
 *
 * For each room the hook:
 *  1. Finds the `BedbankOfferPackageView` matching `room.roomId`
 *  2. Finds the hotel-only, bundle and effective rate matching `room.rateId`
 *  3. Attaches the promotion (if any) and capacity status
 *
 * Rooms that have no selection yet, or whose selection can't be resolved
 * from the current rates, are omitted from the result.
 */
function useBedbankSelectedPackageViewsByRoom(
  offer: App.BedbankOffer | undefined,
  rooms: Array<OfferPageRoom>,
): [Array<RoomWithData> | undefined, boolean] {
  const [packageViewsByRoom, packageViewsLoading] = useBedbankPackageViewsForIndividualRooms(offer, rooms)

  const roomsWithData = useMemo(() => {
    if (packageViewsLoading) {
      return undefined
    }
    const rwd: Array<RoomWithData> = []

    rooms.forEach((room, i) => {
      const packageViews = packageViewsByRoom[i]
      const packageView = packageViews?.find(pv => pv.package.roomId === room.roomId)
      if (!packageView) { return }
      const effectiveRate = packageView.effectiveRates.find(r => r.id === room.rateId)
      if (!effectiveRate) { return }
      const rate = packageView.rates.find(r => r.id === room.rateId) ?? effectiveRate
      const bundleRate = packageView.bundleRates.find(r => r.id === room.rateId) ?? effectiveRate
      const promotion = packageView.promotions[rate?.promotionId ?? '']

      rwd.push({
        room,
        package: packageView.package,
        rate,
        bundleRate,
        effectiveRate,
        promotion,
        capacityValid: packageView.capacityValid,
      })
    })

    return rwd
  }, [packageViewsByRoom, packageViewsLoading, rooms])

  return [roomsWithData, packageViewsLoading]
}

export default useBedbankSelectedPackageViewsByRoom
